import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CodeBracketIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import React, { useState } from 'react';

import { FilterDropdown } from '@/components/filters/FilterDropdown';
import type { FilterOption } from '@/components/filters/types';
import { ClearFiltersButton } from '@/components/legacy-ui/ClearFiltersButton';
import { NoData } from '@/components/legacy-ui/NoData';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Tooltip } from '@/components/Tooltip';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import type { StatusChipVariant } from '@/components/ui/status-chip';
import { StatusChip } from '@/components/ui/status-chip';
import { useEndpoints } from '@/sections/asset/hooks/drawer/useEndpoints';
import type {
  AccessClassification,
  EndpointRow,
} from '@/sections/asset/types/endpoints.types';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';

interface EndpointsSectionProps {
  asset: {
    key?: string;
    identifier?: string;
    details_path?: string;
    detailsPath?: string;
    /** RFC3339 "last seen" — when Vespasian last scanned (drives Last Captured). */
    visited?: string;
  };
}

/**
 * Feature-local HTTP verb → StatusChip variant map (see design-spec §Method-pill
 * colors). Maps to the existing `statusBadgeVariants` — no raw colors.
 */
const METHOD_VARIANTS: Record<string, StatusChipVariant> = {
  GET: 'info',
  POST: 'neutral',
  PUT: 'high',
  DELETE: 'critical',
  PATCH: 'neutral',
};

const methodVariant = (method: string): StatusChipVariant =>
  METHOD_VARIANTS[method.toUpperCase()] ?? 'muted';

/** Access classification → pill label + StatusChip variant. */
const ACCESS_META: Record<
  AccessClassification,
  { label: string; variant: StatusChipVariant }
> = {
  'auth-required': { label: 'Auth Required', variant: 'neutral' },
  'no-auth': { label: 'No Auth', variant: 'critical' },
  inconclusive: { label: 'Inconclusive', variant: 'muted' },
};

/**
 * Shared column grid so the header row and every endpoint row line up:
 * Method · Path (flex) · Parameters · Access · Response
 */
const GRID =
  'grid grid-cols-[120px_minmax(160px,1fr)_104px_128px_128px] items-center gap-3';

/** Explains how the Access value is derived — shown as a header tooltip. */
const ACCESS_HELP = (
  <div className="max-w-[240px] space-y-1 text-left">
    <p>How the endpoint answered Vespasian&apos;s probes:</p>
    <p>
      <span className="font-semibold">Auth Required</span> — returned 401/403.
    </p>
    <p>
      <span className="font-semibold">No Auth</span> — returned 2xx without
      credentials.
    </p>
    <p>
      <span className="font-semibold">Inconclusive</span> — ambiguous (e.g.
      404/5xx).
    </p>
  </div>
);

/** Access pill derived from the endpoint's observed response codes. */
const AccessCell = ({ access }: { access?: AccessClassification }) => {
  const meta = ACCESS_META[access ?? 'inconclusive'];
  return <StatusChip label={meta.label} variant={meta.variant} />;
};

/** StatusChip variant for an HTTP status code, by class (2xx/3xx/4xx/5xx). */
const responseVariant = (code: number): StatusChipVariant => {
  if (code >= 200 && code < 300) return 'neutral';
  if (code >= 300 && code < 400) return 'info';
  if (code >= 400 && code < 500) return 'high';
  if (code >= 500) return 'critical';
  return 'muted';
};

/** Observed HTTP response codes rendered as small class-colored pills. */
const ResponseCell = ({ codes }: { codes?: number[] }) => {
  if (!codes || codes.length === 0) {
    return <span className="text-chariot-text-disabled text-sm">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {codes.map(code => (
        <StatusChip
          key={code}
          label={String(code)}
          variant={responseVariant(code)}
        />
      ))}
    </div>
  );
};

/** Parameter detail table shown in the expanded accordion content. */
const ParameterDetail = ({ row }: { row: EndpointRow }) => {
  if (row.parameterCount === 0) {
    return (
      <p className="text-chariot-text-secondary text-sm">
        No parameters for this endpoint.
      </p>
    );
  }
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="border-chariot-stroke text-chariot-text-secondary border-b-2 text-sm font-semibold">
          <th className="py-2 pr-4">Name</th>
          <th className="py-2 pr-4">In</th>
          <th className="py-2 pr-4">Type</th>
          <th className="py-2">Required</th>
        </tr>
      </thead>
      <tbody>
        {row.parameters.map(p => (
          <tr
            key={`${p.in}:${p.name}`}
            className="border-chariot-stroke border-b text-xs last:border-b-0"
          >
            <td className="text-chariot-text-primary py-2 pr-4 font-mono break-all">
              {p.name}
            </td>
            <td className="text-chariot-text-secondary py-2 pr-4">{p.in}</td>
            <td className="text-chariot-text-secondary py-2 pr-4">{p.type}</td>
            <td className="text-chariot-text-secondary py-2">
              {p.required ? 'Yes' : 'No'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * One endpoint row. The parameter-count button (count + chevron) is the
 * accordion trigger that reveals the parameter detail directly beneath the row.
 */
const EndpointRowItem = ({ row }: { row: EndpointRow }) => (
  <AccordionItem
    value={row.id}
    className="border-chariot-stroke border-b last:border-b-0"
  >
    <div
      className={cn(
        GRID,
        'hover:bg-chariot-background-secondary px-3 py-3 transition-colors'
      )}
    >
      <StatusChip label={row.method} variant={methodVariant(row.method)} />
      <span
        title={row.path}
        className="text-chariot-text-primary min-w-0 truncate font-mono text-xs"
      >
        {row.path}
      </span>
      {row.parameterCount === 0 ? (
        <span className="text-chariot-text-secondary text-center text-sm">
          0 params
        </span>
      ) : (
        <AccordionPrimitive.Header className="flex justify-center">
          <AccordionPrimitive.Trigger
            aria-label={`${row.parameterCount} parameter${
              row.parameterCount === 1 ? '' : 's'
            }`}
            className={cn(
              'text-chariot-text-primary border-chariot-stroke inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
              'hover:bg-chariot-background-tertiary',
              '[&[data-state=open]_.endpoint-chevron]:rotate-180'
            )}
          >
            {row.parameterCount} param{row.parameterCount === 1 ? '' : 's'}
            <ChevronDownIcon className="endpoint-chevron text-chariot-text-secondary size-3.5 shrink-0" />
          </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
      )}
      <AccessCell access={row.access} />
      <ResponseCell codes={row.responseCodes} />
    </div>
    {row.parameterCount > 0 && (
      <AccordionContent className="bg-chariot-background-secondary px-4">
        <ParameterDetail row={row} />
      </AccordionContent>
    )}
  </AccordionItem>
);

const EndpointsSection: React.FC<EndpointsSectionProps> = ({ asset }) => {
  const { endpoints, summary, rawSpec, status, isFetching, error, download } =
    useEndpoints(asset);

  const [viewSpecOpen, setViewSpecOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pathDescending, setPathDescending] = useState(false);
  const [methodFilter, setMethodFilter] = useState<string[]>([]);
  const [accessFilter, setAccessFilter] = useState<string[]>([]);
  const [responseFilter, setResponseFilter] = useState<string[]>([]);

  const hasSpec = Boolean(rawSpec);
  const lastCapturedLabel = summary.lastCaptured
    ? formatDate(summary.lastCaptured)
    : null;
  const hasActiveFilters =
    methodFilter.length > 0 ||
    accessFilter.length > 0 ||
    responseFilter.length > 0;
  const clearAllFilters = () => {
    setMethodFilter([]);
    setAccessFilter([]);
    setResponseFilter([]);
  };

  // Filter options derived from the (unfiltered) endpoint set, so the dropdowns
  // only offer values that actually exist.
  const uniq = <T,>(values: T[]): T[] => Array.from(new Set(values));
  const methodOptions: FilterOption[] = uniq(endpoints.map(e => e.method))
    .sort()
    .map(m => ({ label: m, value: m }));
  const accessOptions: FilterOption[] = uniq(
    endpoints.map(e => e.access ?? 'inconclusive')
  ).map(a => ({ label: ACCESS_META[a].label, value: a }));
  const responseOptions: FilterOption[] = uniq(
    endpoints.flatMap(e => e.responseCodes ?? [])
  )
    .sort((a, b) => a - b)
    .map(c => ({ label: String(c), value: String(c) }));

  const query = search.trim().toLowerCase();
  const rows = [...endpoints]
    .filter(
      row =>
        (!query ||
          row.path.toLowerCase().includes(query) ||
          row.method.toLowerCase().includes(query)) &&
        (methodFilter.length === 0 || methodFilter.includes(row.method)) &&
        (accessFilter.length === 0 ||
          accessFilter.includes(row.access ?? 'inconclusive')) &&
        (responseFilter.length === 0 ||
          (row.responseCodes ?? []).some(code =>
            responseFilter.includes(String(code))
          ))
    )
    .sort((a, b) =>
      pathDescending
        ? b.path.localeCompare(a.path)
        : a.path.localeCompare(b.path)
    );

  const renderBody = () => {
    if (error === 'parse') {
      return (
        <NoData
          styleType="text"
          title="The OpenAPI spec could not be parsed"
          description="You can still download the raw spec above to inspect it."
        />
      );
    }
    if (error === 'too-large') {
      return (
        <NoData
          styleType="text"
          title="The OpenAPI spec is too large to display"
          description="Download it above to inspect."
        />
      );
    }
    if (status === 'pending') {
      return (
        <div data-testid="endpoints-loading" className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="bg-chariot-background-secondary h-9 animate-pulse rounded"
            />
          ))}
        </div>
      );
    }
    if (endpoints.length === 0) {
      return (
        <NoData title="No REST API endpoints discovered for this asset yet" />
      );
    }

    if (rows.length === 0) {
      return (
        <NoData styleType="text" title="No endpoints match your search." />
      );
    }

    return (
      <div>
        {/* Column header — aligned to the accordion rows via the shared grid.
            Borderless (no outer box) with a header underline + row dividers,
            matching the Sitemap table format. Capitalized labels, not all-caps. */}
        <div
          className={cn(
            GRID,
            'border-chariot-stroke text-chariot-text-secondary border-b-2 px-3 py-3 text-sm font-semibold'
          )}
        >
          <span>Method</span>
          <button
            type="button"
            onClick={() => setPathDescending(d => !d)}
            aria-label={`Sort by path ${pathDescending ? 'ascending' : 'descending'}`}
            className="group hover:text-chariot-text-primary flex cursor-pointer items-center gap-1"
          >
            Path
            {pathDescending ? (
              <ChevronDownIcon className="text-chariot-text-primary size-4 shrink-0" />
            ) : (
              <ChevronUpIcon className="text-chariot-text-primary size-4 shrink-0" />
            )}
          </button>
          <span className="text-center">Parameters</span>
          <div className="flex items-center gap-1">
            <span>Access</span>
            <Tooltip title={ACCESS_HELP}>
              <InformationCircleIcon
                aria-label="How Access is determined"
                className="text-chariot-text-tertiary size-3.5 shrink-0 cursor-help"
              />
            </Tooltip>
          </div>
          <span>Response</span>
        </div>
        <Accordion type="multiple" className="w-full">
          {rows.map(row => (
            <EndpointRowItem key={row.id} row={row} />
          ))}
        </Accordion>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Summary stats row — endpoint / method / unauthenticated / last-captured. */}
      {hasSpec && (
        <div
          data-testid="endpoints-summary"
          className="text-chariot-text-secondary flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
        >
          <span>
            <span className="text-chariot-text-primary font-semibold">
              {summary.endpointCount}
            </span>{' '}
            {summary.endpointCount === 1 ? 'Endpoint' : 'Endpoints'}
          </span>
          <span>
            <span className="text-chariot-text-primary font-semibold">
              {summary.methodCount}
            </span>{' '}
            {summary.methodCount === 1 ? 'Method' : 'Methods'}
          </span>
          {summary.unauthenticated !== null ? (
            <span>
              <span className="text-chariot-text-primary font-semibold">
                {summary.unauthenticated}
              </span>{' '}
              Unauthenticated
            </span>
          ) : (
            <Tooltip title="Pending Vespasian data">
              <span className="text-chariot-text-disabled underline decoration-dotted">
                Pending Unauthenticated
              </span>
            </Tooltip>
          )}
          {lastCapturedLabel ? (
            <span>
              Last captured{' '}
              <span className="text-chariot-text-primary font-semibold">
                {lastCapturedLabel}
              </span>
            </span>
          ) : (
            <Tooltip title="Pending Vespasian data">
              <span className="text-chariot-text-disabled underline decoration-dotted">
                Pending Last captured
              </span>
            </Tooltip>
          )}
        </div>
      )}

      {/* Control box — matches the Assets / Risks TableControlsContainer:
          filters + search on the first row, an "Actions:" row (Download / View
          Spec) below a divider. The filters row only renders when there are
          endpoints to filter; the actions row stays available whenever a spec
          exists (e.g. to download an oversized/unparseable spec). */}
      {hasSpec && (
        <div className="border-chariot-stroke bg-chariot-background-secondary overflow-hidden rounded-md border">
          {endpoints.length > 0 && (
            <>
              <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  <h2 className="shrink-0 text-sm font-bold">Filters:</h2>
                  <FilterDropdown
                    id="endpoints-method"
                    label="Method"
                    options={methodOptions}
                    selectedValues={methodFilter}
                    onApply={sel =>
                      setMethodFilter(sel.map(o => String(o.value ?? '')))
                    }
                    onClear={() => setMethodFilter([])}
                    width={220}
                  />
                  <FilterDropdown
                    id="endpoints-access"
                    label="Access"
                    options={accessOptions}
                    selectedValues={accessFilter}
                    onApply={sel =>
                      setAccessFilter(sel.map(o => String(o.value ?? '')))
                    }
                    onClear={() => setAccessFilter([])}
                    width={240}
                  />
                  <FilterDropdown
                    id="endpoints-response"
                    label="Response"
                    options={responseOptions}
                    selectedValues={responseFilter}
                    onApply={sel =>
                      setResponseFilter(sel.map(o => String(o.value ?? '')))
                    }
                    onClear={() => setResponseFilter([])}
                    width={220}
                  />
                  {hasActiveFilters && (
                    <ClearFiltersButton onClick={clearAllFilters} />
                  )}
                </div>
                <div className="w-full sm:w-auto sm:shrink-0">
                  <SearchBar
                    size="sm"
                    className="h-fit w-64 shrink-0 p-1 pb-0"
                    value={search}
                    onChange={setSearch}
                    placeholder="Search endpoints"
                    disabled={status === 'pending'}
                    dataTestId="endpoints-search"
                  />
                </div>
              </div>
              <div className="border-chariot-stroke border-t" />
            </>
          )}
          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            <h2 className="shrink-0 text-sm font-bold">Actions:</h2>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={download}
              disabled={isFetching}
            >
              <ArrowDownTrayIcon className="size-4" />
              Download OpenAPI YAML
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setViewSpecOpen(open => !open)}
              aria-expanded={viewSpecOpen}
            >
              <CodeBracketIcon className="size-4" />
              View Spec
            </Button>
          </div>
        </div>
      )}

      {/* Raw spec — auto-escaped React text only; never raw-HTML injected. */}
      {viewSpecOpen && hasSpec && (
        <pre
          data-testid="endpoints-raw-spec"
          className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary max-h-[32rem] min-h-40 w-full overflow-auto rounded-md border p-4 font-mono text-xs whitespace-pre-wrap"
        >
          {rawSpec}
        </pre>
      )}

      {/* Body: error message, loading, empty, or the endpoint accordion */}
      {renderBody()}
    </div>
  );
};

export default EndpointsSection;
